import { cloneDeep } from 'lodash-es';
import {
  useField,
  useFieldError,
  useFieldValue,
  useFormErrors,
  useIsSubmitting,
  useSetFieldError
} from 'vee-validate';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { hasActiveDialog, showAlert } from '@/composables/useDialog';
import { ErrorViewTypes, RuleNames } from '@/enums/validation.enum';
import type { DetailError, UseValidationParam, ValidationRule } from '@/types/validation.type';

const useValidation = <T>({
  fieldName,
  rules: originalRules,
  showPopupErrorOnSubmitting = true,
  value
}: UseValidationParam) => {
  const formErrors = useFormErrors();
  const isSubmitting = useIsSubmitting();
  const setFieldError = useSetFieldError(fieldName);

  const { t } = useI18n();
  let showPopupError = false;

  const rules = computed(() => {
    const result = cloneDeep(originalRules.value);
    if (typeof result === 'object' && !Array.isArray(result)) {
      for (const ruleName in result) {
        const rule = result[ruleName as RuleNames];
        if (typeof rule === 'object' && !Array.isArray(rule) && 'errorViewType' in rule) {
          delete rule.errorViewType;
        }
        if (typeof rule === 'object' && !Array.isArray(rule) && 'showError' in rule) {
          delete rule.showError;
        }
      }
    }
    return result;
  });

  const { handleBlur, handleChange, setValue, meta, validate } = useField<T>(fieldName, rules);
  const fieldError = useFieldError(fieldName);

  const showError = computed(() => {
    if (!originalRules.value || !fieldError.value || fieldError.value === '') {
      return;
    }
    const error = JSON.parse(fieldError.value);
    return (originalRules.value as any)[error.rule as keyof ValidationRule]?.showError ?? true;
  });

  const fieldValue = useFieldValue(fieldName);
  const hasRequiredError = computed(() => {
    return Object.keys(formErrors.value).some((key: string) => {
      const error = formErrors.value[key]
        ? (JSON.parse(formErrors.value[key]) as DetailError)
        : undefined;
      return error?.rule === RuleNames.REQUIRED;
    });
  });

  const getPopupErrorRuleName = () => {
    const result: string[] = [];
    if (
      typeof originalRules === 'object' &&
      originalRules.value &&
      !Array.isArray(originalRules.value)
    ) {
      for (const ruleName in originalRules.value as Record<RuleNames, ValidationRule>) {
        const rule = (originalRules.value as Record<RuleNames, ValidationRule>)[
          ruleName as RuleNames
        ];
        if (
          typeof rule === 'object' &&
          !Array.isArray(rule) &&
          'errorViewType' in rule &&
          rule.errorViewType === ErrorViewTypes.POPUP
        ) {
          result.push(ruleName);
        }
      }
    }
    return result;
  };

  const errorPopupDisplayable = (error: Record<string, string>) => {
    const ruleNames = getPopupErrorRuleName();
    const isPopupError = ruleNames && ruleNames.includes(error.rule);
    return !showPopupError && error.message && !hasActiveDialog.value && isPopupError;
  };

  const showPopupErrorFunc = async (errors: Partial<Record<string, string>>) => {
    const stringifiedError = errors[fieldName ?? ''] || '';
    const error: Record<string, string> = stringifiedError ? JSON.parse(stringifiedError) : {};
    if (errorPopupDisplayable(error)) {
      showPopupError = true;
      await showAlert({
        content: t(error.message)
      });
      showPopupError = false;
      setFieldError('');
    }
  };

  if (showPopupErrorOnSubmitting) {
    watch(
      () => [formErrors.value, isSubmitting.value],
      async ([errors, submitting]: any) => {
        if (submitting) {
          await showPopupErrorFunc(errors);
        }
      }
    );
  } else {
    watch(
      () => formErrors.value,
      (errors: any) => {
        showPopupErrorFunc(errors);
      }
    );
  }

  watch(
    () => fieldValue.value,
    (v: any) => {
      if (value) {
        value.value = v;
      }
    },
    { immediate: true }
  );

  return {
    hasRequiredError,
    setValue,
    value,
    handleBlur,
    handleChange,
    meta,
    showError,
    validate
  };
};

export default useValidation;
